import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Swords, Trophy, Award, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { useI18n } from "@/contexts/I18nContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";

export default function Landing() {
  const { t, lang, setLang } = useI18n();
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  const isDark = document.documentElement.classList.contains("dark");

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* Header */}
      <header className="glass-nav sticky top-0 z-40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="landing-brand"><Logo /></Link>
          <div className="flex items-center gap-2">
            <button data-testid="lang-toggle" className="text-xs uppercase tracking-widest px-2 py-1 hover:text-primary"
              onClick={() => setLang(lang === "fr" ? "en" : "fr")}>{lang}</button>
            <button data-testid="theme-toggle" className="text-xs uppercase tracking-widest px-2 py-1 hover:text-primary"
              onClick={() => setTheme(isDark ? "light" : "dark")}>{isDark ? "☀" : "☾"}</button>
            {user ? (
              <Link to="/dashboard"><Button size="sm" data-testid="header-cta-app">{t("nav.dashboard")}</Button></Link>
            ) : (
              <>
                <Link to="/login" className="hidden sm:block">
                  <Button size="sm" variant="ghost" data-testid="header-cta-login">{t("nav.login")}</Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" data-testid="header-cta-register">{t("nav.register")}</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-16 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6" data-testid="hero-badge">
              <Sparkles className="w-3 h-3 text-primary" /> CBC Global Holdings Ltd
            </div>
            <h1 className="font-display font-black tracking-tighter leading-[0.9] text-5xl sm:text-6xl lg:text-7xl">
              <span className="block">Globo</span>
              <span className="block">
                {t("landing.tagline")}<span className="font-editorial italic font-normal text-primary">{t("landing.taglineAccent")}</span>
              </span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
              {t("landing.subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to={user ? "/dashboard" : "/register"}>
                <Button size="lg" className="h-12 px-6 text-base" data-testid="hero-cta-primary">
                  {t("landing.cta.primary")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="#features">
                <Button size="lg" variant="outline" className="h-12 px-6 text-base" data-testid="hero-cta-secondary">
                  {t("landing.cta.secondary")}
                </Button>
              </a>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-square rounded-2xl overflow-hidden border border-border shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1611996575749-79a3a250f948?auto=format&fit=crop&w=1200&q=80"
                alt="Globo tactile board"
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-transparent" />
              {/* Floating tile */}
              <div className="absolute bottom-6 left-6 flex gap-1.5">
                {["G", "L", "O", "B", "O"].map((l, i) => (
                  <div key={i} className="tile w-10 h-10 flex items-center justify-center text-lg animate-float-in"
                    style={{ animationDelay: `${i * 100}ms` }}>{l}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t border-border bg-secondary/30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-20">
          <div className="mb-14 max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-3">
              Une plateforme complète
            </div>
            <h2 className="font-display font-extrabold tracking-tight text-4xl lg:text-5xl">
              {t("landing.about.title")}
            </h2>
            <p className="mt-4 text-muted-foreground text-lg leading-relaxed">
              {t("landing.about.body")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Swords, key: "play", testId: "feat-play" },
              { icon: GraduationCap, key: "learn", testId: "feat-learn" },
              { icon: Trophy, key: "rank", testId: "feat-rank" },
              { icon: Award, key: "rewards", testId: "feat-rewards" },
            ].map(({ icon: Icon, key, testId }, i) => (
              <div key={key} className="p-6 rounded-xl bg-card border border-border animate-float-in"
                style={{ animationDelay: `${i * 80}ms` }} data-testid={testId}>
                <div className="w-11 h-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-display font-bold text-lg mb-2">{t(`landing.feature.${key}.title`)}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t(`landing.feature.${key}.body`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-24">
        <div className="rounded-2xl border border-border bg-card p-10 lg:p-14 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="font-display font-extrabold text-3xl lg:text-4xl tracking-tight">
              Rejoignez la première plateforme officielle de <span className="font-editorial italic text-primary">Globo</span>.
            </h3>
            <p className="mt-4 text-muted-foreground">
              Créez votre compte en quelques secondes et affrontez immédiatement d'autres joueurs.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <Link to="/register"><Button size="lg" data-testid="cta-register">{t("nav.register")}</Button></Link>
            <Link to="/login"><Button size="lg" variant="outline" data-testid="cta-login">{t("nav.login")}</Button></Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-8 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} CBC Global Holdings Ltd — <span className="font-editorial italic">Globo, Le Jeu de l'Émergence</span>
      </footer>
    </div>
  );
}
